<template>
  <div>
    <h1>School Performance</h1>
    <p class="text-muted font-weight-bold">
      Overview of school-wide academic performance
    </p>
    <hr />

    <!-- Stats Row -->
    <b-row class="mb-2 hover-card">
      <b-col
        md="6"
        lg="3"
        v-for="(stat, index) in stats"
        :key="index"
        class="mb-1 mb-lg-0"
      >
        <b-card no-body class="h-100 cursor-pointer shadow-sm">
          <b-card-body
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <h6 class="mb-50">{{ stat.title }}</h6>
              <h2
                class="font-weight-bolder mb-0"
                :class="stat.valueClass ? stat.valueClass : 'text-dark'"
              >
                {{ stat.value }}
              </h2>
            </div>
            <div
              class="avatar-content rounded-circle d-flex align-items-center justify-content-center"
              :class="`bg-${stat.variant}`"
              style="width: 48px; height: 48px"
            >
              <feather-icon
                :icon="stat.icon"
                size="24"
                :class="`text-${stat.textVariant}`"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Charts Row -->
    <b-row class="match-height mb-2">
      <!-- Performance by Grade Level -->
      <b-col md="6">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <b-card-title>Performance by Grade Level</b-card-title>
          </b-card-header>
          <b-card-body>
            <vue-apex-charts
              type="bar"
              height="300"
              :options="gradeChartOptions"
              :series="gradeChartSeries"
            />
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Proficiency Trend -->
      <b-col md="6">
        <b-card no-body class="h-100 shadow-sm">
          <b-card-header>
            <b-card-title>Proficiency Trend</b-card-title>
          </b-card-header>
          <b-card-body>
            <vue-apex-charts
              type="line"
              height="300"
              :options="trendChartOptions"
              :series="trendChartSeries"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Class Performance Summary -->
    <b-card no-body>
      <b-card-header>
        <b-card-title>Class Performance Summary</b-card-title>
      </b-card-header>
      <b-card-body>
        <div
          v-for="(item, index) in classPerformance"
          :key="index"
          class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-2 px-1 py-2 border rounded shadow-sm performance-card"
        >
          <!-- Left: Info -->
          <div class="d-flex align-items-center">
            <div>
              <h5 class="mb-50 font-weight-bold text-dark">
                {{ item.subject }}
              </h5>
              <div class="font-small-4">{{ item.details }}</div>
            </div>
          </div>

          <!-- Right: Stats -->
          <div class="d-flex align-items-center mt-1 mt-md-0">
            <div class="mr-2 text-right">
              <small>Average</small>
              <h5 class="mb-0 font-weight-bolder text-dark">
                {{ item.average }}
              </h5>
            </div>

            <div class="mr-2" style="width: 150px">
              <b-progress
                :value="item.proficiency"
                max="100"
                height="8px"
                class="mb-25"
                variant="dark"
              ></b-progress>
              <small class="font-small-3"
                >{{ item.proficiency }}% proficient</small
              >
            </div>

            <b-badge
              pill
              :variant="
                item.status === 'On Track' ? 'success' : 'light-secondary'
              "
              class="px-2 py-50 ml-auto"
            >
              {{ item.status }}
            </b-badge>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BRow,
  BCol,
  BProgress,
  BBadge,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BProgress,
    BBadge,
    VueApexCharts,
  },
  data() {
    return {
      stats: [
        {
          title: "Total Students",
          value: "487",
          icon: "UsersIcon",
          variant: "light",
          textVariant: "dark",
        },
        {
          title: "School Average",
          value: "74.2%",
          icon: "TargetIcon",
          variant: "light-success", // Custom light green bg
          textVariant: "success",
        },
        {
          title: "Proficiency Rate",
          value: "71%",
          icon: "AwardIcon",
          variant: "light-info", // Custom light blue bg
          textVariant: "info",
        },
        {
          title: "Growth Index",
          value: "+4.2%",
          valueClass: "text-success",
          icon: "TrendingUpIcon",
          variant: "light-success",
          textVariant: "success",
        },
      ],
      // Grade Level Chart
      gradeChartSeries: [
        {
          name: "Current",
          data: [72, 78, 75, 71, 69, 74],
        },
        {
          name: "Previous",
          data: [65, 70, 68, 62, 58, 66],
        },
      ],
      gradeChartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
        },
        colors: ["#1e1e2d", "#28C76F"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "65%",
            endingShape: "rounded",
            borderRadius: 4,
          },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["3rd", "4th", "5th", "6th", "7th", "8th"],
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: 80,
          tickAmount: 4,
        },
        grid: {
          borderColor: "#f1f1f1",
          yaxis: { lines: { show: true } },
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + "%";
            },
          },
        },
      },
      // Trend Chart
      trendChartSeries: [
        {
          name: "Proficiency",
          data: [65, 68, 71, 73, 75, 74],
        },
      ],
      trendChartOptions: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#1e1e2d"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: ["Aug", "Sep", "Oct", "Nov", "Dec", "Jan"],
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 60,
          max: 80,
          tickAmount: 4,
        },
        grid: {
          borderColor: "#f1f1f1",
          yaxis: { lines: { show: true } },
        },
        tooltip: {
          x: { show: false },
          y: { formatter: (val) => val + "%" },
        },
      },
      classPerformance: [
        {
          subject: "5th Grade Math",
          details: "Period 1 • 24 students",
          average: "76.5%",
          proficiency: 72,
          status: "Needs Support",
        },
        {
          subject: "5th Grade ELA",
          details: "Period 2 • 24 students",
          average: "79.2%",
          proficiency: 78,
          status: "On Track",
        },
        {
          subject: "5th Grade Science",
          details: "Period 3 • 24 students",
          average: "81%",
          proficiency: 82,
          status: "On Track",
        },
        {
          subject: "5th Grade Social Studies",
          details: "Period 4 • 24 students",
          average: "77.8%",
          proficiency: 75,
          status: "On Track",
        },
      ],
    };
  },
};
</script>

<style scoped>
.bg-light-success {
  background-color: #dcfce7 !important;
}
.bg-light-info {
  background-color: #dbeafe !important;
}
.text-success {
  color: #28c76f !important;
}
.text-info {
  color: #00cfe8 !important;
}

.performance-card {
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
}
.performance-card:hover {
  transform: translateY(-2px);
  /* box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05) !important; */
}
</style>
